// Sprint runner-picker-on-create — choose-on-create modal that opens
// when the founder taps "+". Surfaces remaining capacity % per runner
// so the choice is informed; "Auto" is first + selected by default so
// the existing scheduler behaviour is one click away. Picking a
// specific runner pins the new agent to it via POST /api/agents/empty
// `runnerId`; the backend validates online + not-draining via
// validatePinnedRunner.
//
// Founder-first §5: capacity reasons rendered in plain Spanish; no
// raw status enums leaked. Cursor mirror §2.1: offline/draining
// rows are REPLACED with a one-line plain-language reason rather
// than greyed-out with a "no entry" cursor.
//
// Sprint runner-picker-canonical-usage — capacity source switched from
// heartbeat (`useRunnersStore.cap5hResidualPct`, often defaulted near
// 100 %) to the canonical Anthropic OAuth usage payload that the
// "Runner usage" modal already shows (`useRunnersUsageStore`).
// "% libre" = 100 - max(session 5h, weekly 7d, sonnet weekly) — see
// `lib/runner-capacity.ts` for the policy + tests.

import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from '../i18n';
import { useRunnersUsageStore } from '../stores/runners-usage.store';
import {
  UsageAgeCaption,
  isUsageReadingStale,
  isFleetAgeCaptionSuppressed,
  oldestUsageAtIso,
  usageAgeMs,
  rpcBucketsUsable,
} from './atoms/UsageAgeCaption';
import { useRunnersStore } from '../stores/runners.store';
import type { RunnerUsageEntry } from '../lib/api';
import { deriveCapacity } from '../lib/runner-capacity';
import { classifyRunner } from '../lib/runner-availability';
import Button from '@ds/actions/Button';
// DS.7 D17 — `ds/DsModal` -> `@ds/overlays/Modal` at `size="md"` (560 px).
// The `max-h-[85vh]` fence (sprint `picker-mobile-scroll`, "same class + same
// fix as UsageModal") is RETIRED for the reason it names: the DS `Modal` caps
// its content at `calc(100vh - 128px)` and ships a scrolling `.body` between
// a pinned header and a pinned footer, so the rows panel scrolls and the
// confirm button can no longer land off-screen.
import { DsModal } from './ds/DsModal';
import { deriveRunnerLabel } from './RunnerChip';

interface RunnerPickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Called when the founder confirms. `undefined` = Auto (scheduler).
  onConfirm: (runnerId: string | undefined) => void;
  // Sprint move-runner-button — when set, that runner is filtered out of
  // the list (the agent's current server; moving to it is a no-op). Also
  // lets the caller override the title/confirm copy so the same picker
  // serves both "create here" and "move here".
  excludeRunnerId?: string;
  title?: string;
  confirmLabel?: string;
}

type RowKind = 'auto' | 'runner';

interface RunnerRow {
  kind: RowKind;
  runnerId?: string;
  hostname?: string;
  entry?: RunnerUsageEntry;
  disabledReason?: string;
  // Sprint runner-quarantine-truth (D5, D013-EC-127) — quarantined
  // rows stay SELECTABLE (pinning is the documented founder override
  // of quarantine); the caption makes the override informed.
  quarantined?: boolean;
}

function sortEntries(entries: RunnerUsageEntry[]): RunnerUsageEntry[] {
  // Numeric-aware collation so "runner-10" sorts after "runner-9", not
  // between "runner-1" and "runner-2" (plain localeCompare is
  // lexicographic — the ≥10-runner mis-order class, mirrored from
  // runners.repo.ts::sortRunnersNatural).
  return [...entries].sort((a, b) =>
    a.runnerId.localeCompare(b.runnerId, undefined, { numeric: true, sensitivity: 'base' }),
  );
}

export function RunnerPickerModal({
  open,
  onOpenChange,
  onConfirm,
  excludeRunnerId,
  title,
  confirmLabel,
}: RunnerPickerModalProps) {
  const { t } = useTranslation();
  const entriesByRunnerId = useRunnersUsageStore((s) => s.entriesByRunnerId);
  const hydrate = useRunnersUsageStore((s) => s.hydrate);
  // Sprint runner-quarantine-truth (D5) — usage entries do not carry
  // `unavailableReason`; quarantine visibility needs the runners-store
  // snapshot (same join the usage modal does).
  const runnersById = useRunnersStore((s) => s.runnersById);

  // Auto is the default. Selection survives until close so the
  // founder doesn't lose their pick if they tab away briefly.
  const [selected, setSelected] = useState<string | undefined>(undefined);

  // One-shot CACHE-ONLY refresh on open. Sprint usage-single-emitter
  // (D013-EC-166) dropped the `fresh: true` this used to carry: opening
  // the picker happens once per agent created, and it forced a full
  // fleet fan-out to Anthropic every time.
  //
  // Chesterton (`universal-principles.md` §Chesterton's Fence) — the
  // `fresh: true` comes from sprint `runner-picker-canonical-usage`
  // (D4 + acceptance criterion 1), whose constraint was that the
  // picker's "% libre" must never disagree with the canonical usage
  // modal: a founder-visible SSoT bug from the 2026-06-28 iPhone
  // sign-off. This change is consistent with that constraint because
  // both surfaces read the same store snapshot, so they cannot disagree
  // at any instant. What the forced fetch bought was freshness, not
  // agreement — and freshness is now expressed as a visible age
  // (`UsageAgeCaption`, below).
  useEffect(() => {
    if (!open) return;
    void hydrate({ cacheOnly: true });
    setSelected(undefined);
  }, [open, hydrate]);

  const rows: RunnerRow[] = useMemo(() => {
    const list: RunnerRow[] = [{ kind: 'auto' }];
    const entries = sortEntries(Object.values(entriesByRunnerId));
    for (const e of entries) {
      // Sprint move-runner-button — never offer the agent's current
      // server as a move target (SAME_RUNNER; the backend rejects it too).
      if (excludeRunnerId && e.runnerId === excludeRunnerId) continue;
      // Shared predicate (runner-quarantine-truth D1); falls back to a
      // reason-less classification when the snapshot has not hydrated.
      const availability = classifyRunner(
        runnersById[e.runnerId] ?? {
          status: e.status,
          drainMode: e.drainMode,
          unavailableReason: null,
        },
      );
      let disabledReason: string | undefined;
      if (availability.state === 'offline') {
        disabledReason = t('runnerPicker.offlineReason');
      } else if (availability.state === 'draining') {
        disabledReason = t('runnerPicker.drainingReason');
      }
      list.push({
        kind: 'runner',
        runnerId: e.runnerId,
        hostname: e.hostname,
        entry: e,
        disabledReason,
        quarantined: availability.state === 'quarantined',
      });
    }
    return list;
  }, [entriesByRunnerId, runnersById, t, excludeRunnerId]);

  // Sprint usage-last-known-good (D013-EC-172) D3 — the age of the oldest
  // capacity number actually on screen, computed over the rows the founder
  // can see (the excluded runner is already filtered out above).
  const pickerEntries = useMemo(
    () => rows.flatMap((r) => (r.entry ? [r.entry] : [])),
    [rows],
  );
  const oldestAtIso = useMemo(() => oldestUsageAtIso(pickerEntries), [pickerEntries]);

  function handleConfirm() {
    onConfirm(selected);
    onOpenChange(false);
  }

  return (
    <DsModal
      open={open}
      onClose={() => onOpenChange(false)}
      size="md"
      title={title ?? t('runnerPicker.title')}
      data-testid="runner-picker-modal"
      footer={
        <div className="flex flex-row justify-end gap-2 w-full">
          <Button
            variant="outlined"
            onClick={() => onOpenChange(false)}
            data-testid="runner-picker-cancel"
          >
            {t('runnerPicker.cancel')}
          </Button>
          <Button onClick={handleConfirm} data-testid="runner-picker-confirm">
            {confirmLabel ?? t('runnerPicker.confirm')}
          </Button>
        </div>
      }
    >
        <div
          className="flex flex-col gap-2 py-2"
          data-testid="runner-picker-scroll"
        >
          <p className="text-caption text-muted-foreground">
            {t('runnerPicker.subtitle')}
            {/* Sprint usage-single-emitter (D013-EC-166) D3 — the picker
                no longer forces a fetch on open, so it says how old the
                capacity reading it is showing actually is.
                Sprint usage-last-known-good (D013-EC-172) D3 — and it now
                reports the OLDEST reading in the list rather than the
                store's fleet-level `fetchedAtIso`, which after D1 can be
                minutes old while a given row's numbers are hours old. */}
            {!isFleetAgeCaptionSuppressed(pickerEntries) && (
              <>
                {' · '}
                <UsageAgeCaption atIso={oldestAtIso} data-testid="runner-picker-age" />
              </>
            )}
          </p>

          <div className="flex flex-col gap-1.5" role="radiogroup">
            {rows.map((row) => {
              const isAuto = row.kind === 'auto';
              const id = isAuto ? '__auto__' : row.runnerId!;
              const checked = isAuto ? selected === undefined : selected === id;
              const disabled = !isAuto && !!row.disabledReason;
              return (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={checked}
                  aria-label={isAuto ? t('runnerPicker.autoLabel') : row.hostname}
                  onClick={() => {
                    if (disabled) return;
                    setSelected(isAuto ? undefined : id);
                  }}
                  disabled={disabled}
                  data-testid={
                    isAuto ? 'runner-picker-row-auto' : `runner-picker-row-${id}`
                  }
                  className={
                    'flex items-center gap-3 rounded-md border px-3 py-2.5 text-left ' +
                    'transition-colors ' +
                    (checked
                      ? 'border-accent bg-accent/10'
                      : 'border-border bg-card hover:bg-muted') +
                    (disabled ? ' opacity-60 cursor-not-allowed' : '')
                  }
                >
                  <span
                    aria-hidden="true"
                    className={
                      'shrink-0 w-4 h-4 rounded-full border ' +
                      (checked
                        ? 'border-accent bg-accent'
                        : 'border-border')
                    }
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-body font-medium truncate">
                      {isAuto
                        ? t('runnerPicker.autoLabel')
                        : `${deriveRunnerLabel(row.runnerId!)} · ${row.hostname}`}
                    </div>
                    {isAuto ? (
                      <div className="text-caption text-muted-foreground">
                        {t('runnerPicker.autoHelper')}
                      </div>
                    ) : disabled ? (
                      <div className="text-caption text-muted-foreground">
                        {row.disabledReason}
                      </div>
                    ) : (
                      <>
                        {row.quarantined && (
                          <div
                            className="text-caption text-status-warning"
                            data-testid={`runner-picker-quarantined-${id}`}
                          >
                            {t('runnerPicker.quarantinedCaption')}
                          </div>
                        )}
                        <RunnerCapacity entry={row.entry!} t={t} />
                      </>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

    </DsModal>
  );
}

function RunnerCapacity({
  entry,
  t,
}: {
  entry: RunnerUsageEntry;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  // Sprint runner-picker-session-primary — primary % libre is session 5h
  // (the bucket that gates "can this runner serve my agent now?"),
  // subtitle exposes weekly + sonnet so a runner that's heavily
  // consumed over the longer horizon is still visible at a glance.
  // Sprint usage-from-stream (D6) — `limits[]`/`seven_day_sonnet` are
  // RPC-only forever; gate them on the row's OWN RPC freshness rather
  // than the merged `usage` object's mere presence (D3b iii).
  const capacity = deriveCapacity(entry.usage, { limitsUsable: rpcBucketsUsable(entry) });
  if (capacity === null) {
    // RPC failed for this runner OR session_5h is null — show plain-
    // language "unavailable" instead of a misleading numeric bar.
    return (
      <div className="text-caption text-muted-foreground">
        {t('runnerPicker.capacityUnknown')}
      </div>
    );
  }
  // Sprint usage-last-known-good (D013-EC-172) D3 — past the staleness
  // threshold the capacity renders muted with its age attached, so a
  // carried-forward reading never reads as a fresh recommendation. The
  // picker is where a stale "% libre" does the most damage: it is the
  // screen the founder uses to CHOOSE a runner.
  const stale = isUsageReadingStale(entry.usageAtIso);
  const { primaryFreePct, secondary } = capacity;
  const subtitleParts = secondary.map((b) => {
    if (b.label === 'weekly_scoped' && b.modelName) {
      return t('runnerPicker.bucketWeeklyScopedModel', {
        model: b.modelName,
        percent: b.freePct,
      });
    }
    if (b.label === 'weekly_scoped') {
      return t('runnerPicker.bucketWeeklyScoped', { percent: b.freePct });
    }
    if (b.label === 'weekly') return t('runnerPicker.bucketWeekly', { percent: b.freePct });
    return t('runnerPicker.bucketSonnet', { percent: b.freePct });
  });
  return (
    <div
      className="flex flex-col gap-1"
      data-testid={`runner-picker-capacity-${entry.runnerId}`}
      data-usage-stale={stale ? 'true' : 'false'}
    >
      <div className={`flex items-center gap-2${stale ? ' opacity-50' : ''}`}>
        <div
          aria-hidden="true"
          className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden"
        >
          <div
            className="h-full bg-accent"
            style={{ width: `${primaryFreePct}%` }}
          />
        </div>
        <span className="text-caption text-muted-foreground tabular-nums">
          {t('runnerPicker.capacityFree', { percent: primaryFreePct })}
        </span>
      </div>
      {subtitleParts.length > 0 && (
        <div
          className={`text-caption text-muted-foreground tabular-nums${stale ? ' opacity-50' : ''}`}
        >
          {subtitleParts.join(' · ')}
        </div>
      )}
      {/* Muting is driven by `stale`, which deliberately treats an
          UNPARSEABLE stamp as stale. The caption is driven by
          parseability, because there is no age to state for one — same
          split the modal uses, so no surface captions numbers "No usage
          reading yet" (Gate 2, note 4). */}
      {stale && usageAgeMs(entry.usageAtIso) !== null && (
        <UsageAgeCaption
          atIso={entry.usageAtIso}
          data-testid={`runner-picker-row-age-${entry.runnerId}`}
        />
      )}
    </div>
  );
}
